'use client'

import ConfirmModal from '@/components/confirm-modal'
import SearchableSelect, { SearchableSelectOption } from '@/components/searchable-select'
import {
    AccountingRule,
    CreateAccountingRuleData,
    UpdateAccountingRuleData,
    createAccountingRule,
    deleteAccountingRule,
    getAccountingRules,
    suggestAccountingRules,
    updateAccountingRule,
} from '@/lib/api/financial/financial-accounting-api'
import {
    ChartOfAccountsItem,
    getChartOfAccounts,
} from '@/lib/api/financial/financial-chart-of-accounts-api'
import { useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdDelete,
    MdEdit,
    MdFilterList,
    MdSave,
    MdSmartToy,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

interface RuleForm {
    name: string
    description: string
    priority: string
    billingType: string
    categoryContains: string
    descriptionContains: string
    hasConsultation: string
    hasExam: string
    hasProcedure: string
    hasOrder: string
    paymentMethod: string
    minAmount: string
    maxAmount: string
    debitAccountId: string
    creditAccountId: string
}

const initialForm: RuleForm = {
    name: '',
    description: '',
    priority: '110',
    billingType: '',
    categoryContains: '',
    descriptionContains: '',
    hasConsultation: '',
    hasExam: '',
    hasProcedure: '',
    hasOrder: '',
    paymentMethod: '',
    minAmount: '',
    maxAmount: '',
    debitAccountId: '',
    creditAccountId: '',
}

export default function AccountingRulesPage() {
    const navigate = useNavigate()
    const [rules, setRules] = useState<AccountingRule[]>([])
    const [accounts, setAccounts] = useState<ChartOfAccountsItem[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    // Modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<RuleForm>(initialForm)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Sugestões IA
    const [suggesting, setSuggesting] = useState(false)
    const [suggestions, setSuggestions] = useState<any[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)
        try {
            const [rulesRes, accountsRes] = await Promise.all([
                getAccountingRules(),
                getChartOfAccounts(),
            ])
            if (rulesRes.success && rulesRes.result) setRules(rulesRes.result)
            if (accountsRes.success && accountsRes.result) setAccounts(accountsRes.result)
        } catch {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setForm(initialForm)
        modalRef.current?.showModal()
    }

    const openEditModal = (rule: AccountingRule) => {
        setEditingId(rule.id)
        setForm({
            name: rule.name,
            description: rule.description || '',
            priority: String(rule.priority),
            billingType: rule.billingType || '',
            categoryContains: rule.categoryContains || '',
            descriptionContains: rule.descriptionContains || '',
            hasConsultation: rule.hasConsultation === null ? '' : rule.hasConsultation ? 'true' : 'false',
            hasExam: rule.hasExam === null ? '' : rule.hasExam ? 'true' : 'false',
            hasProcedure: rule.hasProcedure === null ? '' : rule.hasProcedure ? 'true' : 'false',
            hasOrder: rule.hasOrder === null ? '' : rule.hasOrder ? 'true' : 'false',
            paymentMethod: rule.paymentMethod || '',
            minAmount: rule.minAmount ? String(rule.minAmount / 100) : '',
            maxAmount: rule.maxAmount ? String(rule.maxAmount / 100) : '',
            debitAccountId: String(rule.debitAccountId),
            creditAccountId: String(rule.creditAccountId),
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setForm(initialForm)
    }

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.warning('Informe o nome da regra')
            return
        }
        if (!form.debitAccountId || !form.creditAccountId) {
            toast.warning('Selecione as contas de débito e crédito')
            return
        }

        setSaving(true)
        try {
            const parseBool = (v: string) => v === '' ? undefined : v === 'true'

            const data: CreateAccountingRuleData = {
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                priority: parseInt(form.priority, 10) || 0,
                billingType: form.billingType || undefined,
                categoryContains: form.categoryContains.trim() || undefined,
                descriptionContains: form.descriptionContains.trim() || undefined,
                hasConsultation: parseBool(form.hasConsultation),
                hasExam: parseBool(form.hasExam),
                hasProcedure: parseBool(form.hasProcedure),
                hasOrder: parseBool(form.hasOrder),
                paymentMethod: form.paymentMethod || undefined,
                minAmount: form.minAmount ? Math.round(parseFloat(form.minAmount) * 100) : undefined,
                maxAmount: form.maxAmount ? Math.round(parseFloat(form.maxAmount) * 100) : undefined,
                debitAccountId: parseInt(form.debitAccountId, 10),
                creditAccountId: parseInt(form.creditAccountId, 10),
            }

            let response
            if (editingId) {
                response = await updateAccountingRule(editingId, data as UpdateAccountingRuleData)
            } else {
                response = await createAccountingRule(data)
            }

            if (response.success) {
                toast.success(editingId ? 'Regra atualizada' : 'Regra criada')
                closeModal()
                loadData()
            } else {
                toast.error(response.error || 'Erro ao salvar regra')
            }
        } catch {
            toast.error('Erro ao salvar regra')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const response = await deleteAccountingRule(deleteId)
            if (response.success) {
                toast.success('Regra excluída')
                setDeleteId(null)
                loadData()
            } else {
                toast.error(response.error || 'Erro ao excluir')
            }
        } catch {
            toast.error('Erro ao excluir regra')
        } finally {
            setDeleting(false)
        }
    }

    const handleToggleActive = async (rule: AccountingRule) => {
        try {
            const response = await updateAccountingRule(rule.id, { active: !rule.active })
            if (response.success) {
                toast.success(rule.active ? 'Regra desativada' : 'Regra ativada')
                loadData()
            }
        } catch {
            toast.error('Erro ao alterar status')
        }
    }

    const handleSuggestRules = async () => {
        setSuggesting(true)
        setSuggestions([])
        setShowSuggestions(true)
        try {
            const res = await suggestAccountingRules()
            if (res.success && res.result) {
                if (res.result.message) {
                    toast.info(res.result.message)
                    setShowSuggestions(false)
                } else if (res.result.suggestions.length === 0) {
                    toast.info('A IA não encontrou padrões para sugerir novas regras.')
                    setShowSuggestions(false)
                } else {
                    setSuggestions(res.result.suggestions)
                    toast.success(`${res.result.suggestions.length} sugestão(ões) encontrada(s)`)
                }
            } else {
                toast.error('Erro ao gerar sugestões')
                setShowSuggestions(false)
            }
        } catch {
            toast.error('Erro ao consultar IA')
            setShowSuggestions(false)
        } finally {
            setSuggesting(false)
        }
    }

    const handleAcceptSuggestion = async (suggestion: any) => {
        try {
            const res = await createAccountingRule(suggestion)
            if (res.success) {
                toast.success(`Regra "${suggestion.name}" criada`)
                setSuggestions(prev => prev.filter(s => s !== suggestion))
                loadData()
            }
        } catch {
            toast.error('Erro ao criar regra')
        }
    }

    const activeAccounts = accounts.filter(a => a.active)

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">Regras de Classificação</h1>
                        <p className="text-base-content/60 text-sm mt-1">
                            Regras para classificação automática de lançamentos contábeis
                        </p>
                    </div>
                </div>
                <button className="btn btn-secondary" onClick={openCreateModal}>
                    <MdAdd size={20} />
                    Nova Regra
                </button>
                <button
                    className="btn btn-secondary btn-sm gap-1"
                    onClick={handleSuggestRules}
                    disabled={suggesting}
                >
                    {suggesting ? <span className="loading loading-spinner loading-xs"></span> : <MdSmartToy size={18} />}
                    Sugerir via IA
                </button>
            </div>

            {/* Sugestões da IA */}
            {showSuggestions && (
                <div className="card bg-secondary/5 border border-secondary/30 shadow-sm mb-4">
                    <div className="card-body p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-sm flex items-center gap-2">
                                <MdSmartToy size={16} />
                                Sugestões da IA
                            </h3>
                            <button className="btn btn-ghost btn-xs" onClick={() => setShowSuggestions(false)}>Fechar</button>
                        </div>
                        {suggesting && (
                            <div className="flex items-center gap-2 py-4 justify-center">
                                <span className="loading loading-spinner loading-sm text-secondary"></span>
                                <span className="text-sm text-base-content/60">Analisando lançamentos...</span>
                            </div>
                        )}
                        {!suggesting && suggestions.length === 0 && (
                            <p className="text-sm text-base-content/50">Nenhuma sugestão disponível.</p>
                        )}
                        {suggestions.map((s, i) => {
                            const debitAcc = accounts.find(a => a.id === s.debitAccountId)
                            const creditAcc = accounts.find(a => a.id === s.creditAccountId)
                            return (
                                <div key={i} className="flex items-start justify-between py-2 border-b border-base-300/30 last:border-0">
                                    <div>
                                        <p className="text-sm font-medium">{s.name}</p>
                                        {s.description && <p className="text-xs text-base-content/60">{s.description}</p>}
                                        <div className="flex gap-3 mt-1 text-xs">
                                            {debitAcc && <span><span className="text-error font-medium">D:</span> {debitAcc.code} - {debitAcc.title}</span>}
                                            {creditAcc && <span><span className="text-success font-medium">C:</span> {creditAcc.code} - {creditAcc.title}</span>}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-success btn-xs"
                                        onClick={() => handleAcceptSuggestion(s)}
                                    >
                                        Aceitar
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center min-h-[300px]">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {/* Empty */}
            {!loading && rules.length === 0 && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body items-center text-center py-16">
                        <MdFilterList size={48} className="text-base-content/30" />
                        <p className="text-base-content/60 mt-2">Nenhuma regra cadastrada</p>
                        <p className="text-xs text-base-content/40 mt-1">
                            Regras classificam cobranças automaticamente no plano de contas
                        </p>
                        <button className="btn btn-secondary btn-sm mt-4" onClick={openCreateModal}>
                            <MdAdd size={18} />
                            Criar Primeira Regra
                        </button>
                    </div>
                </div>
            )}

            {/* Rules List */}
            {!loading && rules.length > 0 && (
                <div className="flex flex-col gap-3">
                    {rules.map(rule => (
                        <div
                            key={rule.id}
                            className={`card bg-base-100 shadow-sm border ${rule.active ? 'border-base-300/50' : 'border-base-300/30 opacity-60'}`}
                        >
                            <div className="card-body p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="badge badge-sm badge-outline">#{rule.priority}</span>
                                            <h3 className="font-semibold">{rule.name}</h3>
                                            {!rule.active && (
                                                <span className="badge badge-xs badge-ghost">Inativa</span>
                                            )}
                                        </div>
                                        {rule.description && (
                                            <p className="text-sm text-base-content/60 mb-2">{rule.description}</p>
                                        )}

                                        {/* Condições */}
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {rule.billingType && (
                                                <span className="badge badge-xs badge-outline">
                                                    {rule.billingType === 'RECEIVABLE' ? 'A Receber' : 'A Pagar'}
                                                </span>
                                            )}
                                            {rule.hasConsultation && <span className="badge badge-xs badge-outline">Consulta</span>}
                                            {rule.hasExam && <span className="badge badge-xs badge-outline">Exame</span>}
                                            {rule.hasProcedure && <span className="badge badge-xs badge-outline">Procedimento</span>}
                                            {rule.hasOrder && <span className="badge badge-xs badge-outline">Pedido</span>}
                                            {rule.categoryContains && (
                                                <span className="badge badge-xs badge-outline">Categoria: {rule.categoryContains}</span>
                                            )}
                                            {rule.paymentMethod && (
                                                <span className="badge badge-xs badge-outline">{rule.paymentMethod}</span>
                                            )}
                                        </div>

                                        {/* Contas */}
                                        <div className="flex items-center gap-4 text-xs">
                                            <span>
                                                <span className="text-error font-medium">D:</span>{' '}
                                                {rule.debitAccount.code} - {rule.debitAccount.title}
                                            </span>
                                            <span>
                                                <span className="text-success font-medium">C:</span>{' '}
                                                {rule.creditAccount.code} - {rule.creditAccount.title}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-sm toggle-secondary"
                                            checked={rule.active}
                                            onChange={() => handleToggleActive(rule)}
                                        />
                                        <button
                                            className="btn btn-ghost btn-xs"
                                            onClick={() => openEditModal(rule)}
                                        >
                                            <MdEdit size={14} />
                                        </button>
                                        <button
                                            className="btn btn-ghost btn-xs text-error"
                                            onClick={() => setDeleteId(rule.id)}
                                        >
                                            <MdDelete size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-2xl">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar Regra' : 'Nova Regra'}
                    </h3>

                    <div className="space-y-4">
                        {/* Nome e Prioridade */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2 form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Nome <span className="text-error">*</span></span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: Receita de Consultas"
                                    value={form.name}
                                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Prioridade</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="110"
                                    value={form.priority}
                                    onChange={e => setForm(prev => ({ ...prev, priority: e.target.value }))}
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Maior valor é avaliada primeiro
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Descrição</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Descrição opcional"
                                value={form.description}
                                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>

                        <div className="divider text-sm font-medium">Condições</div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Tipo de Cobrança</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.billingType}
                                    onChange={e => setForm(prev => ({ ...prev, billingType: e.target.value }))}
                                >
                                    <option value="">Qualquer</option>
                                    <option value="RECEIVABLE">A Receber (Entrada)</option>
                                    <option value="PAYABLE">A Pagar (Saída)</option>
                                </select>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Método de Pagamento</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.paymentMethod}
                                    onChange={e => setForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                                >
                                    <option value="">Qualquer</option>
                                    <option value="PIX">PIX</option>
                                    <option value="BOLETO">Boleto</option>
                                    <option value="CREDIT_CARD">Cartão de Crédito</option>
                                    <option value="DEBIT_CARD">Cartão de Débito</option>
                                    <option value="CASH">Dinheiro</option>
                                    <option value="BANK_TRANSFER">Transferência</option>
                                </select>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Categoria contém</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Texto parcial"
                                    value={form.categoryContains}
                                    onChange={e => setForm(prev => ({ ...prev, categoryContains: e.target.value }))}
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Descrição contém</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Texto parcial"
                                    value={form.descriptionContains}
                                    onChange={e => setForm(prev => ({ ...prev, descriptionContains: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                            {[
                                { key: 'hasConsultation', label: 'Consulta' },
                                { key: 'hasExam', label: 'Exame' },
                                { key: 'hasProcedure', label: 'Procedimento' },
                                { key: 'hasOrder', label: 'Pedido' },
                            ].map(({ key, label }) => (
                                <div key={key} className="form-control">
                                    <label className="label">
                                        <span className="label-text text-xs">{label}</span>
                                    </label>
                                    <select
                                        className="select select-bordered select-sm w-full"
                                        value={(form as any)[key]}
                                        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                                    >
                                        <option value="">Indiferente</option>
                                        <option value="true">Sim</option>
                                        <option value="false">Não</option>
                                    </select>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Valor mínimo (R$)</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="input input-bordered w-full"
                                    placeholder="0,00"
                                    value={form.minAmount}
                                    onChange={e => setForm(prev => ({ ...prev, minAmount: e.target.value }))}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Valor máximo (R$)</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="input input-bordered w-full"
                                    placeholder="0,00"
                                    value={form.maxAmount}
                                    onChange={e => setForm(prev => ({ ...prev, maxAmount: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="divider text-sm font-medium">Classificação</div>

                        <div className="grid grid-cols-2 gap-4">
                            {(() => {
                                const TYPE_LABELS: Record<string, string> = { ASSET: 'Ativo', LIABILITY: 'Passivo', EQUITY: 'Patrimônio', INCOME: 'Receita', EXPENSE: 'Despesa' }
                                const TYPE_BADGE: Record<string, string> = { ASSET: 'badge-secondary text-white', LIABILITY: 'badge-warning text-white', EQUITY: 'badge-secondary text-white', INCOME: 'badge-success text-white', EXPENSE: 'badge-error text-white' }
                                const accountOptions: SearchableSelectOption[] = activeAccounts.map(a => ({
                                    value: String(a.id),
                                    label: `${a.code} - ${a.title}`,
                                    badge: TYPE_LABELS[a.type] || a.type,
                                    badgeClass: TYPE_BADGE[a.type] || 'badge-ghost',
                                    group: TYPE_LABELS[a.type] || a.type,
                                }))
                                return (
                                    <>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Conta Débito <span className="text-error">*</span>
                                                </span>
                                            </label>
                                            <SearchableSelect
                                                options={accountOptions}
                                                value={form.debitAccountId}
                                                onChange={val => setForm(prev => ({ ...prev, debitAccountId: val }))}
                                                placeholder="Selecionar conta"
                                                searchPlaceholder="Buscar por código ou nome..."
                                                noResultsText="Nenhuma conta encontrada"
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Conta Crédito <span className="text-error">*</span>
                                                </span>
                                            </label>
                                            <SearchableSelect
                                                options={accountOptions}
                                                value={form.creditAccountId}
                                                onChange={val => setForm(prev => ({ ...prev, creditAccountId: val }))}
                                                placeholder="Selecionar conta"
                                                searchPlaceholder="Buscar por código ou nome..."
                                                noResultsText="Nenhuma conta encontrada"
                                            />
                                        </div>
                                    </>
                                )
                            })()}
                        </div>
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdSave size={18} />
                            )}
                            {editingId ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Delete Modal */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Regra"
                message="Tem certeza de que deseja excluir esta regra de classificação?"
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
